
import React, { useState, useEffect, useMemo } from 'react';
import PageHeader from '../components/shared/PageHeader';
import { getBlogPostSummaries } from '../services/apiService';
import type { BlogPostSummary } from '../services/apiService';
import { AlertTriangle, Loader, Search, ChevronDown, Grid, List } from 'react-feather';
import Pagination from '../components/ui/Pagination';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import Seo from '../components/shared/Seo';
import AnimatedSection from '../components/ui/AnimatedSection';
import { generateItemListSchema } from '../lib/schema';
import { useData } from '../context/DataContext';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../components/ui/LocalizedLink';

const BlogPage: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const [posts, setPosts] = useState<BlogPostSummary[] | null | undefined>(undefined);
  const { authors, firmInfo } = useData();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'alpha'>('newest');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const authorsMap = useMemo(() => {
    return authors.reduce((acc, author) => {
      acc[author.id] = author.name;
      return acc;
    }, {} as Record<string, string>);
  }, [authors]);

  useEffect(() => {
    // 2026-10-04, found via a Semrush audit: this used to skip loading posts for automated browsers
    // (navigator.webdriver), citing a hydration mismatch that cannot occur (index.tsx uses createRoot,
    // not hydrateRoot). Netlify's Prerender renders crawler requests in exactly such a browser, so
    // every crawler and AI bot saw an empty spinner instead of the article list. Always load.
    (window as any).prerenderReady = false;
    getBlogPostSummaries({ language: i18n.language })
      .then(data => setPosts(data))
      .catch(err => {
        console.error("Failed to load blog posts", err);
        setPosts(null);
      });
  }, [i18n.language]);

  // Tell Netlify's Prerender (the crawler-facing headless render) the list is really on the page.
  useEffect(() => {
    if (posts !== undefined) (window as any).prerenderReady = true;
    return () => { (window as any).prerenderReady = undefined; };
  }, [posts]);

  // Every post in this language, newest first, ignoring search/pagination -- rendered as plain links
  // below so crawlers (which cannot click through pages 2..N) can reach every article.
  const archivePosts = useMemo(() => {
    if (!posts) return [];
    return posts
      .filter(post => (post.language || 'en') === i18n.language)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [posts, i18n.language]);

  const filteredAndSortedPosts = useMemo(() => {
    if (!posts) return [];
    
    // Filter posts by the current language, defaulting to 'en' for backward compatibility.
    let result = posts.filter(post => (post.language || 'en') === i18n.language);

    if (searchTerm.trim() !== '') {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(post =>
        post.title.toLowerCase().includes(lowercasedTerm) ||
        post.summary.toLowerCase().includes(lowercasedTerm)
      );
    }

    switch (sortOrder) {
      case 'oldest':
        result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 'alpha':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'newest':
      default:
        result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
    }

    return result;
  }, [posts, searchTerm, sortOrder, i18n.language]);

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredAndSortedPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredAndSortedPosts.length / postsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const breadcrumbs = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.blog'), path: '/blog' }
  ];

  const itemListItems = currentPosts.map(post => ({ 
    name: post.title, 
    url: `${firmInfo.url}${i18n.language === 'es' ? '/es' : ''}/blog/${post.slug.replace(/\/$/, '')}/`,
    description: post.summary 
  }));
  const itemListSchema = generateItemListSchema(itemListItems, "The Action Firm Blog Articles");

  const renderContent = () => {
    if (posts === undefined) {
      return (
        <div className="flex justify-center items-center h-64">
          <Loader className="animate-spin text-brand-gold" size={48} />
          <p className="ml-4">{t('common.loading')}</p>
        </div>
      );
    }

    if (posts === null) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-center text-red-400">
          <AlertTriangle size={48} className="mb-4" />
          <h3 className="text-xl font-semibold">{t('blogPage.loadErrorTitle')}</h3>
          <p className="text-red-400/80">{t('blogPage.loadErrorMessage')}</p>
        </div>
      );
    }

    if (currentPosts.length === 0) {
      return (
        <div className="text-center py-20">
          <h2 className="text-2xl font-semibold">{t('blogPage.noPostsTitle')}</h2>
          <p className="text-white/70 mt-2">{t('blogPage.noPostsMessage')}</p>
        </div>
      );
    }

    return (
      <AnimatedSection>
        <div className="grid grid-cols-1 gap-12">
          {currentPosts.map((post) => {
            const cleanedSlug = post.slug.replace(/\/$/, '');
            const postUrl = `/blog/${cleanedSlug}`;

            return (
              <article
                key={post.slug}
                className="grid md:grid-cols-3 gap-8 items-center"
              >
                <LocalizedLink to={postUrl} className="md:col-span-1 group block overflow-hidden rounded-2xl">
                  <img
                    src={post.imageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover aspect-video md:aspect-[4/3] transition-transform duration-300 group-hover:scale-105"
                    width="400"
                    height="300"
                    loading="lazy"
                  />
                </LocalizedLink>
                <div className="md:col-span-2">
                  <h2 className="text-2xl lg:text-3xl font-bold font-serif mt-2">
                    <LocalizedLink to={postUrl} className="hover:text-white transition-colors">{post.title}</LocalizedLink>
                  </h2>
                  <p className="mt-4 text-white/80 line-clamp-3">{post.summary}</p>
                  <div className="mt-4 text-sm text-white/60">
                    <span>{t('blogPage.byAuthor')} {authorsMap[post.authorId] || 'The Action Firm'}</span>
                    <span className="mx-2">·</span>
                    <span>{new Date(post.date).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </AnimatedSection>
    );
  };


  return (
    <>
      <Seo
        title={t('blogPage.seoTitle')}
        description={t('blogPage.seoDescription')}
        schema={[itemListSchema]}
      />
      <noscript>
        <h2>Recent Articles from The Action Firm Blog</h2>
        {posts && posts.slice(0, 6).map(post => (
          <div key={post.slug}>
            <h3>{post.title}</h3>
            <p>{post.summary}</p>
          </div>
        ))}
      </noscript>
      <div>
        <PageHeader
          title={t('blogPage.pageTitle')}
          subtitle={t('blogPage.pageSubtitle')}
          imageUrl="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=1600&fm=webp"
        />
        <Breadcrumbs crumbs={breadcrumbs} />
        
        <div className="py-24">
          <div className="mx-auto max-w-7xl px-6">
            {/* Controls */}
            <div className="mb-12 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="relative w-full md:w-1/3">
                <input
                  type="text"
                  placeholder={t('blogPage.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 pl-10 pr-4 py-3 text-white placeholder-white/50 outline-none focus:border-yellow-400/60 focus:ring-1 focus:ring-yellow-400"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" size={20} />
              </div>
              <div className="flex items-center gap-4">
                <div className="relative">
                   <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest' | 'alpha')}
                    className="w-full appearance-none rounded-xl border border-white/10 bg-white/5 pl-4 pr-10 py-3 text-white outline-none focus:border-yellow-400/60 focus:ring-1 focus:ring-yellow-400"
                  >
                    <option value="newest">{t('blogPage.sortNewest')}</option>
                    <option value="oldest">{t('blogPage.sortOldest')}</option>
                    <option value="alpha">{t('blogPage.sortAlpha')}</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/50" size={20} />
                </div>
              </div>
            </div>

            {renderContent()}

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}

            {archivePosts.length > 0 && (
              <nav aria-label={i18n.language === 'es' ? 'Todos los artículos' : 'All articles'} className="mt-20 border-t border-white/10 pt-12">
                <h2 className="text-2xl font-serif font-bold">{i18n.language === 'es' ? 'Todos los artículos' : 'All articles'}</h2>
                <ul className="mt-6 grid gap-x-10 gap-y-3 md:grid-cols-2 text-white/80">
                  {archivePosts.map(post => (
                    <li key={post.slug}>
                      <LocalizedLink to={`/blog/${post.slug.replace(/\/$/, '')}`} className="hover:text-white hover:underline underline-offset-4">
                        {post.title}
                      </LocalizedLink>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;